import { relations, sql } from 'drizzle-orm';
import { index, jsonb, pgEnum, pgSequence, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core';
import { user } from './auth.js';
import { patientProtocols, patientTherapySequence, patientTherapySequenceMedicine } from './patient-therapy.js';
import { patientGfrs } from './patients.js';
import { uuidv7 } from './utils/uuidv7.js';
import * as z from 'zod';
export const actionType = pgEnum('action_type', ['create', 'update', 'delete', 'access']);
export const eventLogStatus = pgEnum('event_log_status', ['OK', 'FAILED', 'WARNING', 'INFO']);
export const eventLogs = pgTable('event_log', {
    id: uuidv7('id').primaryKey(),
    domain: text('domain'),
    resource: text('resource'),
    resourceId: uuid('resource_id'),
    event: text('event'),
    action: actionType('action'),
    patches: jsonb('patches').$type(),
    ipAddress: text('ip_address'),
    status: eventLogStatus('status'),
    details: jsonb('details').$type(),
    search: text('search'),
    impersonatedBy: text('impersonated_by'),
    performedBy: uuid('performed_by').references(() => user.id, { onDelete: 'no action' }),
    performedAt: timestamp('performed_at').notNull(),
}, t => [
    // every resource drawer/activity query filters resource + resourceId and orders by time
    index('idx_event_log_resource_resource_performed').on(t.resource, t.resourceId, t.performedAt),
    // system log: cursor pagination over performedAt + id
    index('idx_event_log_performed_id').on(t.performedAt, t.id),
    index('idx_event_log_performed_by').on(t.performedBy, t.performedAt),
    // the system-log name filter uses ILIKE on the search text
    index('idx_event_log_search_trgm').using('gin', sql `${t.search} gin_trgm_ops`),
]);
export const eventLogRelations = relations(eventLogs, ({ one }) => ({
    actor: one(user, {
        fields: [eventLogs.performedBy],
        references: [user.id],
    }),
    // resourceId is polymorphic: each relation is only meaningful for its matching resource.
    patientProtocol: one(patientProtocols, {
        fields: [eventLogs.resourceId],
        references: [patientProtocols.id],
    }),
    patientTherapySequence: one(patientTherapySequence, {
        fields: [eventLogs.resourceId],
        references: [patientTherapySequence.id],
    }),
    patientTherapySequenceMedicine: one(patientTherapySequenceMedicine, {
        fields: [eventLogs.resourceId],
        references: [patientTherapySequenceMedicine.id],
    }),
    patientGfr: one(patientGfrs, {
        fields: [eventLogs.resourceId],
        references: [patientGfrs.id],
    }),
}));
export const HL7Status = z.enum(['OK', 'FAILED', 'SKIPPED']);
export const hl7Status = pgEnum('hl7_status', [HL7Status.enum.OK, HL7Status.enum.FAILED, HL7Status.enum.SKIPPED]);
export const HL7Direction = z.enum(['INBOUND', 'OUTBOUND']);
export const hl7Direction = pgEnum('hl7_direction', [HL7Direction.enum.INBOUND, HL7Direction.enum.OUTBOUND]);
// Source of the numeric part of outbound MSH-10 (`CYPM` + 10-digit zero-padded value), so every
// outbound message gets a unique control ID that its ACK (MSA-2) can be matched against. AMMS
// rejects an MSH-10 it has already seen (MSA-1 CR), so this starts above the legacy Cypro 1 range
// (~CYPM0108125721) and below the IDs Cypro 2.0 derived from prescription ids (CYPM26…).
export const hl7OutboundControlSeq = pgSequence('hl7_outbound_control_seq', { startWith: 1_000_000_000, increment: 1 });
export const HL7EventLog = pgTable('hl7_event_log', {
    id: uuidv7('id').primaryKey(),
    message: text('resource').notNull(),
    createdAt: timestamp('created_at').notNull().defaultNow(),
    status: hl7Status('status'),
    direction: hl7Direction('direction'),
    errorMessage: text('error_message'),
    resourceRef: jsonb('resource_ref').$type(),
    /** MSH-10 of `message`: ours for outbound rows, the sender's for inbound rows. ACK MSA-2 matches it. */
    messageControlId: text('message_control_id'),
    /** Raw ACK received for an outbound message (written by Mirth, or by the built-in sender). */
    ack: text('ack'),
    /** MSA-1 of `ack`, e.g. CA, AA, AE, AR, CE, CR. */
    ackCode: text('ack_code'),
    ackReceivedAt: timestamp('ack_received_at'),
}, t => [
    index('idx_hl7_event_log_message_control_id').on(t.messageControlId),
    // the event log list pages by createdAt and filters direction/status
    index('idx_hl7_event_log_created').on(t.createdAt, t.id),
    index('idx_hl7_event_log_direction_status_created').on(t.direction, t.status, t.createdAt),
]);
//# sourceMappingURL=event-log.js.map